import React, { useEffect, useMemo, useState } from "react";
import { StepProps } from "../../types/onboarding";
import styles from "./SelectIntegrationsVC.module.css";
import vcStyles from "../vc-onboarding/VCOnboarding.module.css";
import InlineSpinner from "../../components/common/InlineSpinner";
import { useOnboarding } from "../../hooks/useOnboarding";
import { useAuth } from "../../contexts/AuthContext";
import onboardingService from "../../services/onboardingService";
import { integrationsService } from "../../services/integrationsService";
import IntegrationLogo from "../../components/common/IntegrationLogo";
import {
  INTEGRATIONS,
  INTEGRATION_CATEGORIES,
  IntegrationCategory,
} from "../../types/vcOnboarding";
import PostHogInfoModal from "../../components/integrations/PostHogInfoModal";
import AmplitudeInfoModal from "../../components/integrations/AmplitudeInfoModal";
import MixpanelInfoModal from "../../components/integrations/MixpanelInfoModal";
import IntegrationDataSelectionModal from "../../components/integrations/IntegrationDataSelectionModal";
import { DataSelections } from "../../types/integrationOptions";
import { COMPOSIO_IDS } from "../../config/integrations";
import { ComposioService } from "../../services/composioService";

// Mapping of categories to their integration IDs
const CATEGORY_INTEGRATION_MAP: Record<IntegrationCategory, string[]> = {
  banking: ["plaid", "stripe", "quickbooks", "xero", "shopify"],
  revenue: [],
  engineering: ["github", "gitlab", "linear", "jira", "notion", "bitbucket", "asana", "trello", "monday", "clickup"],
  product_analytics: ["posthog", "amplitude", "mixpanel", "segment", "heap", "datadog", "sentry"],
  crm_sales: ["hubspot", "salesforce", "pipedrive", "close", "attio", "freshdesk"],
  communication: ["slack", "intercom", "zendesk", "gmail", "microsoft-teams", "discord"],
  productivity: ["google-calendar", "google-drive", "confluence", "dropbox"],
};

interface Integration {
  id: string;
  name: string;
  icon?: string;
  domain?: string;
  category?: string;
}

// Map the global INTEGRATIONS to the local format
const ALL_INTEGRATIONS: Integration[] = INTEGRATIONS.map((i) => ({
  id: i.id,
  name: i.name,
  icon: i.logo || i.icon,
  domain: i.domain,
  category: i.category,
}));

// Remove duplicates by id (in case any overlap)
const UNIQUE_INTEGRATIONS = ALL_INTEGRATIONS.filter(
  (integration, index, self) =>
    index === self.findIndex((t) => t.id === integration.id),
);

// Category information for the informative screen (use INTEGRATION_CATEGORIES for consistency)
const CATEGORY_INFO: Record<
  string,
  { title: string; icon: string; description: string }
> = {
  banking: {
    title: "Banking & Revenue",
    icon: "💰",
    description: "Connect banking and payment tools to track financial metrics",
  },
  engineering: {
    title: "Engineering & Build",
    icon: "⚡",
    description:
      "Monitor development velocity, code commits, and engineering output",
  },
  product_analytics: {
    title: "Product Analytics",
    icon: "📊",
    description: "Track user engagement, DAU/MAU, and product metrics",
  },
  crm_sales: {
    title: "CRM & Sales",
    icon: "🤝",
    description:
      "Monitor sales pipeline, deal flow, and customer relationships",
  },
  communication: {
    title: "Communication",
    icon: "💬",
    description: "Integrate team communication and support channels",
  },
};

// Minimum integrations required to proceed
const MINIMUM_INTEGRATIONS_REQUIRED = 2;

const SelectIntegrationsVC: React.FC<StepProps> = ({ onNext, onBack }) => {
  const { user } = useAuth();
  const { data, setIntegrations } = useOnboarding();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selected = data.integrations || [];
  const [connectingId, setConnectingId] = useState<string | null>(null);
  const [composioToolkits, setComposioToolkits] = useState<Integration[]>([]);
  const [vcSearchTerm, setVcSearchTerm] = useState("");
  const [vcCurrentPage, setVcCurrentPage] = useState(1);
  const vcItemsPerPage = 50;
  const [connectedProviders, setConnectedProviders] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPostHogModal, setShowPostHogModal] = useState(false);
  const [pendingPostHogConnect, setPendingPostHogConnect] = useState(false);
  const [showAmplitudeModal, setShowAmplitudeModal] = useState(false);
  const [pendingAmplitudeConnect, setPendingAmplitudeConnect] = useState(false);
  const [showMixpanelModal, setShowMixpanelModal] = useState(false);
  const [pendingMixpanelConnect, setPendingMixpanelConnect] = useState(false);
  const [showDataSelectionModal, setShowDataSelectionModal] = useState(false);
  const [dataSelectionProvider, setDataSelectionProvider] = useState<
    string | null
  >(null);

  // Dual-screen state: 1 = informative screen, 2 = integration selection
  const [currentScreen, setCurrentScreen] = useState<1 | 2>(1);

  // VC Required Categories and VC name
  const [requiredCategories, setRequiredCategories] = useState<
    IntegrationCategory[]
  >([]);
  const [vcName, setVcName] = useState<string>("Your VC partner");

  // Load VC required categories and VC name from localStorage
  useEffect(() => {
    const vcRequired = localStorage.getItem("vc_required_categories");
    if (vcRequired) {
      try {
        const parsed = JSON.parse(vcRequired);
        setRequiredCategories(parsed);
      } catch (err) {
        console.error("Error parsing VC required categories:", err);
      }
    }

    const vcInviteData = localStorage.getItem("portfolio_invite_data");
    if (vcInviteData) {
      try {
        const parsed = JSON.parse(vcInviteData);
        if (parsed.vcName) {
          setVcName(parsed.vcName);
        }
        // Also check for requiredCategories in invite data
        if (parsed.requiredCategories) {
          setRequiredCategories(parsed.requiredCategories);
        }
      } catch (err) {
        console.error("Error parsing VC invite data:", err);
      }
    }
  }, []);

  // Helper function to check if a category requirement is satisfied
  const isCategorySatisfied = (category: IntegrationCategory): boolean => {
    const categoryIntegrations = CATEGORY_INTEGRATION_MAP[category];
    return categoryIntegrations.some((integrationId) =>
      connectedProviders.some(
        (p) => p.toLowerCase() === integrationId.toLowerCase(),
      ),
    );
  };

  // Helper function to get the category title
  const getCategoryTitle = (categoryId: IntegrationCategory): string => {
    const category = INTEGRATION_CATEGORIES.find((c) => c.id === categoryId);
    return category?.title || categoryId;
  };

  // Load connected integrations so the user sees status and we gate Next
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!user) return;
        const list: any[] = await integrationsService.getUserIntegrations(true);
        if (!mounted) return;
        const active = (list || [])
          .filter((i: any) => i?.integration_status === "active")
          .map((i: any) => i.provider.toLowerCase());
        setConnectedProviders(active);
        setIntegrations(active);
      } catch (e) {
        // ignore
      }
    })();
    return () => {
      mounted = false;
    };
  }, [user, setIntegrations]);

  // Load all Composio toolkits dynamically
  useEffect(() => {
    (async () => {
      try {
        const toolkits = await ComposioService.listToolkits();
        const normalize = (s: string) => s.toLowerCase().replace(/[-_\s]+/g, "");
        const staticIds = new Set(UNIQUE_INTEGRATIONS.map((i) => normalize(i.id)));
        const seen = new Set<string>();
        const dynamic: Integration[] = toolkits
          .map((t: any) => ({ ...t, _id: (t.key || t.appId || t.name || "").toLowerCase() }))
          .filter((t: any) => {
            if (!t._id) return false;
            const norm = normalize(t._id);
            if (staticIds.has(norm) || seen.has(norm)) return false;
            seen.add(norm);
            return true;
          })
          .map((t: any) => ({
            id: t._id,
            name: t.name || t.key || "Unknown",
            icon: t.logo || undefined,
            domain: undefined,
            category: (t.categories?.[0]?.toLowerCase() || "communication") as string,
          }));
        setComposioToolkits(dynamic);
      } catch { /* ignore */ }
    })();
  }, []);

  // Reset page on search
  useEffect(() => { setVcCurrentPage(1); }, [vcSearchTerm]);

  // Providers we can connect now via OAuth
  const connectableProviders = new Set([
    "github",
    "gitlab",
    "jira",
    "slack",
    "google-analytics",
    "notion",
    "linear",
    "posthog",
    "amplitude",
    "mixpanel",
    "hubspot",
    "zendesk",
    "intercom",
    "stripe",
  ]);

  // Dynamic Composio toolkit IDs are always connectable
  const composioToolkitIds = new Set(composioToolkits.map((t) => t.id));

  // Providers that are disabled (shown but not clickable)
  const disabledProviders = new Set(["plaid"]);

  // Providers that need repository or data selection after connection
  const PROVIDERS_WITH_DATA_SELECTION = new Set([
    "github",
    "gitlab",
    "slack",
    "jira",
    "hubspot",
    "zendesk",
    "intercom",
  ]);

  const refreshProviders = async () => {
    const list = await integrationsService.getUserIntegrations(true);
    const active = (list || [])
      .filter((i: any) => i?.integration_status === "active")
      .map((i: any) => i.provider.toLowerCase());
    setConnectedProviders(active);
    setIntegrations(active);
  };

  const handleTileClick = async (id: string) => {
    setError(null);
    if (connectableProviders.has(id) || composioToolkitIds.has(id)) {
      // Special handling for PostHog - show info modal first
      if (id === "posthog") {
        setShowPostHogModal(true);
        setPendingPostHogConnect(true);
        return;
      }

      // Special handling for Amplitude - show info modal first
      if (id === "amplitude") {
        setShowAmplitudeModal(true);
        setPendingAmplitudeConnect(true);
        return;
      }

      // Special handling for Mixpanel - show info modal first
      if (id === "mixpanel") {
        setShowMixpanelModal(true);
        setPendingMixpanelConnect(true);
        return;
      }

      // Route Composio integrations through ComposioService
      if (COMPOSIO_IDS.has(id) || composioToolkitIds.has(id)) {
        try {
          setConnectingId(id);
          const result = await ComposioService.initiateConnection(id);
          const redirectUrl = result.redirectUrl;
          if (redirectUrl) {
            const w = 500, h = 700;
            const left = window.screenX + (window.outerWidth - w) / 2;
            const top = window.screenY + (window.outerHeight - h) / 2;
            window.open(redirectUrl, `composio_connect_${id}`, `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes`);
            // Mirrors the poll in SelectIntegrations.tsx: retain the last
            // failure so a timeout can explain itself instead of going silent.
            let attempts = 0;
            let lastError: string | null = null;
            let settled = false;

            const finish = async () => {
              if (settled) return;
              settled = true;
              clearInterval(poll);
              window.removeEventListener("message", onPopupMessage);
              setConnectingId(null);
              setError(null);
              await refreshProviders();
            };

            // The callback page posts here as soon as Composio hands the popup
            // back; polling remains the fallback.
            const onPopupMessage = (event: MessageEvent) => {
              if (event.origin !== window.location.origin) return;
              if (event.data?.source !== "composio") return;
              if (event.data?.type !== "OAUTH_SUCCESS") return;
              void finish();
            };
            window.addEventListener("message", onPopupMessage);

            const poll = setInterval(async () => {
              attempts++;
              try {
                const status = await ComposioService.getConnectionStatus(id);
                if (status.connected) {
                  await finish();
                  return;
                }
                lastError = status.error || null;
              } catch (err) {
                lastError = err instanceof Error ? err.message : "Status check failed";
              }
              if (attempts >= 30 && !settled) {
                settled = true;
                clearInterval(poll);
                window.removeEventListener("message", onPopupMessage);
                setConnectingId(null);
                setError(
                  lastError
                    ? `Couldn't confirm the connection: ${lastError}`
                    : "Connection is still being processed. Refresh in a moment to check."
                );
                try {
                  await refreshProviders();
                } catch {
                  // Already surfacing a message above; a failed refresh adds nothing.
                }
              }
            }, 2000);
          }
        } catch (err) {
          setConnectingId(null);
          setError(err instanceof Error ? err.message : "Failed to connect");
        }
        return;
      }

      try {
        setConnectingId(id);
        await integrationsService.connectIntegration(id, {
          onSuccess: async () => {
            console.log(`✅ Successfully connected ${id}`);

            const checkConnection = async (): Promise<boolean> => {
              try {
                const integration =
                  await integrationsService.syncProviderConnection(
                    id.toLowerCase(),
                  );
                if (
                  integration &&
                  integration.integration_status === "active"
                ) {
                  const list =
                    await integrationsService.getUserIntegrations();
                  const active = (list || [])
                    .filter((i) => i?.integration_status === "active")
                    .map((i) => i.provider.toLowerCase());
                  setConnectedProviders(active);
                  setIntegrations(active);
                  console.log(
                    `Connection verified for ${id} via sync endpoint`,
                  );
                  return true;
                }
              } catch (e) {
                console.warn(
                  "Sync check failed, falling back to polling:",
                  e,
                );
              }
              for (let attempt = 0; attempt < 3; attempt++) {
                await new Promise((r) => setTimeout(r, 500));
                try {
                  const list =
                    await integrationsService.getUserIntegrations();
                  const active = (list || [])
                    .filter((i) => i?.integration_status === "active")
                    .map((i) => i.provider.toLowerCase());
                  if (
                    active.some(
                      (p) => p.toLowerCase() === id.toLowerCase(),
                    )
                  ) {
                    setConnectedProviders(active);
                    setIntegrations(active);
                    return true;
                  }
                } catch (error) {
                  console.error(
                    `Fallback retry ${attempt + 1} failed:`,
                    error,
                  );
                }
              }
              return false;
            };

            const connected = await checkConnection();
            setConnectingId(null);

            if (!connected) {
              console.error(
                `❌ Connection for ${id} failed verification after all retries`,
              );
              setError(
                `Failed to verify ${id} connection. Please try reconnecting or contact support.`,
              );
            } else if (PROVIDERS_WITH_DATA_SELECTION.has(id.toLowerCase())) {
              setDataSelectionProvider(id.toLowerCase());
              setShowDataSelectionModal(true);
            }
          },
          onError: (error: Error) => {
            console.error(`❌ Failed to connect ${id}:`, error);
            setError(
              error?.message || `Failed to connect ${id}. Please try again.`,
            );
            setConnectingId(null);
          },
          onClose: () => {
            console.log(`ℹ️ User closed ${id} connection dialog`);
            setConnectingId(null);
          },
        });
      } catch (e: any) {
        setError(e?.message || `Failed to connect ${id}. Please try again.`);
        setConnectingId(null);
      }
      return;
    }
  };

  const handlePostHogContinue = async () => {
    setShowPostHogModal(false);
    if (pendingPostHogConnect) {
      setPendingPostHogConnect(false);
      try {
        setConnectingId("posthog");
        await integrationsService.connectIntegration("posthog", {
          onSuccess: async () => {
            console.log(`✅ Successfully connected posthog`);

            const checkConnection = async (): Promise<boolean> => {
              try {
                const integration =
                  await integrationsService.syncProviderConnection("posthog");
                if (
                  integration &&
                  integration.integration_status === "active"
                ) {
                  const list =
                    await integrationsService.getUserIntegrations();
                  const active = (list || [])
                    .filter((i) => i?.integration_status === "active")
                    .map((i) => i.provider.toLowerCase());
                  setConnectedProviders(active);
                  setIntegrations(active);
                  console.log(
                    `Connection verified for posthog via sync endpoint`,
                  );
                  return true;
                }
              } catch (e) {
                console.warn(
                  "Sync check failed, falling back to polling:",
                  e,
                );
              }
              for (let attempt = 0; attempt < 3; attempt++) {
                await new Promise((r) => setTimeout(r, 500));
                try {
                  const list =
                    await integrationsService.getUserIntegrations();
                  const active = (list || [])
                    .filter((i) => i?.integration_status === "active")
                    .map((i) => i.provider.toLowerCase());
                  if (active.some((p) => p.toLowerCase() === "posthog")) {
                    setConnectedProviders(active);
                    setIntegrations(active);
                    return true;
                  }
                } catch (error) {
                  console.error(
                    `Fallback retry ${attempt + 1} failed:`,
                    error,
                  );
                }
              }
              return false;
            };

            const connected = await checkConnection();
            setConnectingId(null);

            if (!connected) {
              console.error(
                `❌ Connection for posthog failed verification after all retries`,
              );
              setError(
                `Failed to verify posthog connection. Please try reconnecting or contact support.`,
              );
            } else if (PROVIDERS_WITH_DATA_SELECTION.has("posthog")) {
              setDataSelectionProvider("posthog");
              setShowDataSelectionModal(true);
            }
          },
          onError: (error: Error) => {
            console.error(`❌ Failed to connect posthog:`, error);
            setError(
              error?.message || `Failed to connect posthog. Please try again.`,
            );
            setConnectingId(null);
          },
          onClose: () => {
            console.log(`ℹ️ User closed posthog connection dialog`);
            setConnectingId(null);
          },
        });
      } catch (e: any) {
        setError(e?.message || `Failed to connect posthog. Please try again.`);
        setConnectingId(null);
      }
    }
  };

  const handlePostHogClose = () => {
    setShowPostHogModal(false);
    setPendingPostHogConnect(false);
  };

  const handleAmplitudeContinue = async () => {
    setShowAmplitudeModal(false);
    if (pendingAmplitudeConnect) {
      setPendingAmplitudeConnect(false);
      try {
        setConnectingId("amplitude");
        await integrationsService.connectIntegration("amplitude", {
          onSuccess: async () => {
            console.log(`✅ Successfully connected amplitude`);

            const checkConnection = async (): Promise<boolean> => {
              try {
                const integration =
                  await integrationsService.syncProviderConnection(
                    "amplitude",
                  );
                if (
                  integration &&
                  integration.integration_status === "active"
                ) {
                  const list =
                    await integrationsService.getUserIntegrations();
                  const active = (list || [])
                    .filter((i) => i?.integration_status === "active")
                    .map((i) => i.provider.toLowerCase());
                  setConnectedProviders(active);
                  setIntegrations(active);
                  console.log(
                    `Connection verified for amplitude via sync endpoint`,
                  );
                  return true;
                }
              } catch (e) {
                console.warn(
                  "Sync check failed, falling back to polling:",
                  e,
                );
              }
              for (let attempt = 0; attempt < 3; attempt++) {
                await new Promise((r) => setTimeout(r, 500));
                try {
                  const list =
                    await integrationsService.getUserIntegrations();
                  const active = (list || [])
                    .filter((i) => i?.integration_status === "active")
                    .map((i) => i.provider.toLowerCase());
                  if (
                    active.some((p) => p.toLowerCase() === "amplitude")
                  ) {
                    setConnectedProviders(active);
                    setIntegrations(active);
                    return true;
                  }
                } catch (error) {
                  console.error(
                    `Fallback retry ${attempt + 1} failed:`,
                    error,
                  );
                }
              }
              return false;
            };

            const connected = await checkConnection();
            setConnectingId(null);

            if (!connected) {
              console.error(
                `❌ Connection for amplitude failed verification after all retries`,
              );
              setError(
                `Failed to verify amplitude connection. Please try reconnecting or contact support.`,
              );
            } else if (PROVIDERS_WITH_DATA_SELECTION.has("amplitude")) {
              setDataSelectionProvider("amplitude");
              setShowDataSelectionModal(true);
            }
          },
          onError: (error: Error) => {
            console.error(`❌ Failed to connect amplitude:`, error);
            setError(
              error?.message ||
                `Failed to connect amplitude. Please try again.`,
            );
            setConnectingId(null);
          },
          onClose: () => {
            console.log(`ℹ️ User closed amplitude connection dialog`);
            setConnectingId(null);
          },
        });
      } catch (e: any) {
        setError(
          e?.message || `Failed to connect amplitude. Please try again.`,
        );
        setConnectingId(null);
      }
    }
  };

  const handleAmplitudeClose = () => {
    setShowAmplitudeModal(false);
    setPendingAmplitudeConnect(false);
  };

  const handleMixpanelContinue = async () => {
    setShowMixpanelModal(false);
    if (pendingMixpanelConnect) {
      setPendingMixpanelConnect(false);
      try {
        setConnectingId("mixpanel");
        await integrationsService.connectIntegration("mixpanel", {
          onSuccess: async () => {
            console.log(`✅ Successfully connected mixpanel`);

            const checkConnection = async (): Promise<boolean> => {
              try {
                const integration =
                  await integrationsService.syncProviderConnection("mixpanel");
                if (
                  integration &&
                  integration.integration_status === "active"
                ) {
                  const list =
                    await integrationsService.getUserIntegrations();
                  const active = (list || [])
                    .filter((i) => i?.integration_status === "active")
                    .map((i) => i.provider.toLowerCase());
                  setConnectedProviders(active);
                  setIntegrations(active);
                  console.log(
                    `Connection verified for mixpanel via sync endpoint`,
                  );
                  return true;
                }
              } catch (e) {
                console.warn(
                  "Sync check failed, falling back to polling:",
                  e,
                );
              }
              for (let attempt = 0; attempt < 3; attempt++) {
                await new Promise((r) => setTimeout(r, 500));
                try {
                  const list =
                    await integrationsService.getUserIntegrations();
                  const active = (list || [])
                    .filter((i) => i?.integration_status === "active")
                    .map((i) => i.provider.toLowerCase());
                  if (active.some((p) => p.toLowerCase() === "mixpanel")) {
                    setConnectedProviders(active);
                    setIntegrations(active);
                    return true;
                  }
                } catch (error) {
                  console.error(
                    `Fallback retry ${attempt + 1} failed:`,
                    error,
                  );
                }
              }
              return false;
            };

            const connected = await checkConnection();
            setConnectingId(null);

            if (!connected) {
              console.error(
                `❌ Connection for mixpanel failed verification after all retries`,
              );
              setError(
                `Failed to verify mixpanel connection. Please try reconnecting or contact support.`,
              );
            } else if (PROVIDERS_WITH_DATA_SELECTION.has("mixpanel")) {
              setDataSelectionProvider("mixpanel");
              setShowDataSelectionModal(true);
            }
          },
          onError: (error: Error) => {
            console.error(`❌ Failed to connect mixpanel:`, error);
            setError(
              error?.message || `Failed to connect mixpanel. Please try again.`,
            );
            setConnectingId(null);
          },
          onClose: () => {
            console.log(`ℹ️ User closed mixpanel connection dialog`);
            setConnectingId(null);
          },
        });
      } catch (e: any) {
        setError(e?.message || `Failed to connect mixpanel. Please try again.`);
        setConnectingId(null);
      }
    }
  };

  const handleMixpanelClose = () => {
    setShowMixpanelModal(false);
    setPendingMixpanelConnect(false);
  };

  const handleDataSelectionSave = async (selections: DataSelections) => {
    if (!dataSelectionProvider) return;

    try {
      await integrationsService.updateIntegrationSettings(
        dataSelectionProvider,
        {
          selectedRepositories: selections.selectedIds,
          selectedKinds: selections.selectedKinds,
          idToNameMap: selections.idToNameMap || {},
          lastConfigured: new Date().toISOString(),
        },
      );

      console.log(`✅ Saved data selections for ${dataSelectionProvider}`);
      setShowDataSelectionModal(false);
      setDataSelectionProvider(null);
    } catch (err: any) {
      console.error(
        `❌ Failed to save selections for ${dataSelectionProvider}:`,
        err,
      );
      setError(
        err?.message ||
          `Failed to save ${dataSelectionProvider} selections. Please try again.`,
      );
      setShowDataSelectionModal(false);
      setDataSelectionProvider(null);
    }
  };

  // Get integrations for a specific category
  const getIntegrationsForCategory = (category: IntegrationCategory) => {
    const categoryIntegrationIds = CATEGORY_INTEGRATION_MAP[category];
    return UNIQUE_INTEGRATIONS.filter((integration) =>
      categoryIntegrationIds.includes(integration.id),
    );
  };

  // Group integrations by category for Screen 2
  const integrationsByCategory = useMemo(() => {
    return {
      banking: UNIQUE_INTEGRATIONS.filter((i) =>
        CATEGORY_INTEGRATION_MAP.banking.includes(i.id),
      ),
      engineering: UNIQUE_INTEGRATIONS.filter((i) =>
        CATEGORY_INTEGRATION_MAP.engineering.includes(i.id),
      ),
      product_analytics: UNIQUE_INTEGRATIONS.filter((i) =>
        CATEGORY_INTEGRATION_MAP.product_analytics.includes(i.id),
      ),
      crm_sales: UNIQUE_INTEGRATIONS.filter((i) =>
        CATEGORY_INTEGRATION_MAP.crm_sales.includes(i.id),
      ),
      communication: UNIQUE_INTEGRATIONS.filter((i) =>
        CATEGORY_INTEGRATION_MAP.communication.includes(i.id),
      ),
    };
  }, []);

  // Filter integrations based on search term
  const filteredIntegrationsByCategory = useMemo(() => {
    if (!searchTerm) return integrationsByCategory;

    const filtered: Record<string, Integration[]> = {};
    Object.entries(integrationsByCategory).forEach(
      ([category, integrations]) => {
        const matches = integrations.filter((i) =>
          i.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        if (matches.length > 0) {
          filtered[category] = matches;
        }
      },
    );
    return filtered;
  }, [integrationsByCategory, searchTerm]);

  const handleNext = async () => {
    if (!user) {
      setError("User not found. Please log in again.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Check for VC required categories - must have at least one integration from each category
      if (requiredCategories.length > 0) {
        const unsatisfiedCategories = requiredCategories.filter(
          (category) => !isCategorySatisfied(category),
        );

        if (unsatisfiedCategories.length > 0) {
          const missingCategoryNames = unsatisfiedCategories
            .map((categoryId) => getCategoryTitle(categoryId))
            .join(", ");

          throw new Error(
            `${vcName} requires at least one integration from: ${missingCategoryNames}`,
          );
        }
      }

      // Also check minimum 2 integrations requirement
      if (connectedProviders.length < MINIMUM_INTEGRATIONS_REQUIRED) {
        throw new Error(
          `Please connect at least ${MINIMUM_INTEGRATIONS_REQUIRED} integrations to continue. You have connected ${connectedProviders.length}.`,
        );
      }

      // Save to Supabase onboarding data
      const currentOnboardingData = {
        ...data,
        integrations: connectedProviders,
        step: 5, // Next step in VC invite flow
        completed_steps: [
          ...(data.completed_steps || []),
          "integrations_selection",
        ],
      };

      await onboardingService.updateOnboarding(user.id, currentOnboardingData);

      onNext && onNext(selected);
    } catch (error) {
      console.error("Failed to save integrations:", error);
      setError(
        (error as any)?.message ||
          "Failed to save your integrations. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if an integration belongs to a required category
  const isInRequiredCategory = (integrationId: string): boolean => {
    return requiredCategories.some((category) => {
      const categoryIntegrations = CATEGORY_INTEGRATION_MAP[category];
      return categoryIntegrations.includes(integrationId);
    });
  };

  const renderIntegrationCard = (integration: Integration) => {
    const isConnectable = connectableProviders.has(integration.id) || composioToolkitIds.has(integration.id);
    const isDisabled = disabledProviders.has(integration.id);
    const isInRequired = isInRequiredCategory(integration.id);
    const isConnected = connectedProviders.some(
      (p) => p.toLowerCase() === integration.id.toLowerCase(),
    );
    const isConnecting = connectingId === integration.id;

    return (
      <button
        key={integration.id}
        type="button"
        className={`${vcStyles.integrationCard} ${isConnected ? vcStyles.selected : ""} ${!isConnectable || isDisabled ? styles.tileDisabled : ""}`}
        onClick={() =>
          isConnectable && !isDisabled && handleTileClick(integration.id)
        }
        disabled={!isConnectable || isConnecting || isDisabled}
        style={{
          border: isConnected
            ? "2px solid #10b981"
            : isInRequired
              ? "2px solid #667eea"
              : undefined,
          position: "relative",
          opacity: isConnectable && !isDisabled ? 1 : 0.5,
          cursor: isConnectable && !isDisabled ? "pointer" : "not-allowed",
        }}
      >
        {/* Show "Connected" badge if connected */}
        {isConnected && (
          <span
            style={{
              position: "absolute",
              top: "4px",
              right: "4px",
              background: "#10b981",
              color: "white",
              padding: "2px 6px",
              fontSize: "10px",
              fontWeight: 600,
              borderRadius: "3px",
              zIndex: 1,
            }}
          >
            Connected
          </span>
        )}

        {isConnecting && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
            }}
          >
            <InlineSpinner />
          </div>
        )}
        <div className={vcStyles.integrationContent}>
          <div className={vcStyles.integrationIcon}>
            <IntegrationLogo
              domain={integration.domain}
              name={integration.name}
              fallbackImage={integration.icon}
              className={`w-full h-full ${
                ["mercury", "ramp", "amplitude", "pipedrive"].includes(
                  integration.id,
                )
                  ? vcStyles.iconInvert
                  : ""
              }`}
            />
          </div>
          <div className={vcStyles.integrationName}>
            {isConnecting ? "Connecting..." : integration.name}
          </div>
        </div>
        <div className={vcStyles.integrationCheckbox}>
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      </button>
    );
  };

  // Screen 1: Informative screen showing categories selected by VC
  const renderInformativeScreen = () => (
    <div className={styles.root}>
      <h1 className={styles.header}>Connect Your Tools</h1>
      <p className={styles.subheader}>
        {vcName} has requested data from the following categories to track your
        company&apos;s progress. You&apos;ll choose which specific tools to
        connect in the next step.
      </p>

      {/* What happens next alert - moved above category grid */}
      <div className={vcStyles.completionBanner}>
        <div className={vcStyles.completionIcon}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
              stroke="#c4b5fd"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <div className={vcStyles.completionText}>
          <div className={vcStyles.completionTitle}>What happens next?</div>
          <div className={vcStyles.completionDescription}>
            You&apos;ll be able to select and connect specific tools from each
            category. Connect at least {MINIMUM_INTEGRATIONS_REQUIRED}{" "}
            integrations to generate meaningful insights for your updates.
          </div>
        </div>
      </div>

      {requiredCategories.length > 0 ? (
        <div className={vcStyles.categoryGrid}>
          {requiredCategories.map((categoryId) => {
            const info = CATEGORY_INFO[categoryId];
            if (!info) return null;

            return (
              <button
                key={categoryId}
                type="button"
                className={vcStyles.categoryCard}
                onClick={() => setCurrentScreen(2)}
                style={{ cursor: "pointer" }}
              >
                <div className={vcStyles.categoryCardContent}>
                  <div className={vcStyles.categoryCardIcon}>{info.icon}</div>
                  <div className={vcStyles.categoryCardInfo}>
                    <div className={vcStyles.categoryCardTitle}>
                      {info.title}
                    </div>
                    <div className={vcStyles.categoryCardDescription}>
                      {info.description}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        // If no VC requirements, show all categories
        <div className={vcStyles.categoryGrid}>
          {Object.entries(CATEGORY_INFO).map(([categoryId, info]) => (
            <button
              key={categoryId}
              type="button"
              className={vcStyles.categoryCard}
              onClick={() => setCurrentScreen(2)}
              style={{ cursor: "pointer" }}
            >
              <div className={vcStyles.categoryCardContent}>
                <div className={vcStyles.categoryCardIcon}>{info.icon}</div>
                <div className={vcStyles.categoryCardInfo}>
                  <div className={vcStyles.categoryCardTitle}>{info.title}</div>
                  <div className={vcStyles.categoryCardDescription}>
                    {info.description}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className={styles.footer}>
        {onBack && (
          <button className={styles.secondaryButton} onClick={onBack}>
            Back
          </button>
        )}
        <button
          className={styles.primaryButton}
          onClick={() => setCurrentScreen(2)}
        >
          Continue
        </button>
      </div>
    </div>
  );

  // Screen 2: Integration selection UI (reusing VC onboarding style)
  const renderIntegrationSelectionScreen = () => (
    <div className={styles.root}>
      <h1 className={styles.header}>Select Your Integrations</h1>
      <p className={styles.subheader}>
        Choose which tools to connect. You must connect at least{" "}
        {MINIMUM_INTEGRATIONS_REQUIRED} integrations.
      </p>

      <div
        className={vcStyles.integrationCard}
        style={{
          marginBottom: "1.5rem",
          background: "#1e1b4b",
          borderColor: "#5b21b6",
        }}
      >
        <p style={{ fontSize: "0.875rem", color: "#c4b5fd", margin: 0 }}>
          {connectedProviders.length} of {MINIMUM_INTEGRATIONS_REQUIRED} minimum
          integrations connected
          {connectedProviders.length >= MINIMUM_INTEGRATIONS_REQUIRED && " ✓"}
        </p>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      {/* PostHog Info Modal */}
      <PostHogInfoModal
        isOpen={showPostHogModal}
        onClose={handlePostHogClose}
        onContinue={handlePostHogContinue}
      />

      <AmplitudeInfoModal
        isOpen={showAmplitudeModal}
        onClose={handleAmplitudeClose}
        onContinue={handleAmplitudeContinue}
      />

      <MixpanelInfoModal
        isOpen={showMixpanelModal}
        onClose={handleMixpanelClose}
        onContinue={handleMixpanelContinue}
      />

      {/* Integration Data Selection Modal */}
      <IntegrationDataSelectionModal
        isOpen={showDataSelectionModal}
        onClose={() => {
          setShowDataSelectionModal(false);
          setDataSelectionProvider(null);
        }}
        onSave={handleDataSelectionSave}
        provider={dataSelectionProvider || ""}
        providerName={
          dataSelectionProvider
            ? UNIQUE_INTEGRATIONS.find(
                (i) =>
                  i.id.toLowerCase() === dataSelectionProvider.toLowerCase(),
              )?.name || dataSelectionProvider
            : ""
        }
      />

      {/* Search bar */}
      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder={`Search ${[...UNIQUE_INTEGRATIONS, ...composioToolkits].length}+ integrations...`}
          value={vcSearchTerm}
          onChange={(e) => setVcSearchTerm(e.target.value)}
          className={styles.searchInput}
          style={{ width: "100%", maxWidth: "500px", padding: "10px 16px", fontSize: "14px" }}
        />
      </div>

      {/* Required Categories Section - show each category with its integrations */}
      {!vcSearchTerm && requiredCategories.length > 0 &&
        requiredCategories.map((categoryId) => {
          const categoryInfo = INTEGRATION_CATEGORIES.find(
            (c) => c.id === categoryId,
          );
          const categoryIntegrations = getIntegrationsForCategory(categoryId);
          const satisfied = isCategorySatisfied(categoryId);

          return (
            <div key={categoryId} className={vcStyles.categorySection}>
              <div
                className={vcStyles.categoryTitle}
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <span className={vcStyles.categoryIcon}>
                  {categoryInfo?.icon}
                </span>
                {categoryInfo?.title || categoryId}
                {satisfied && (
                  <span
                    style={{
                      marginLeft: "0.5rem",
                      background: "#10b981",
                      color: "white",
                      padding: "2px 8px",
                      fontSize: "11px",
                      fontWeight: 600,
                      borderRadius: "4px",
                    }}
                  >
                    ✓ Satisfied
                  </span>
                )}
              </div>
              <p
                style={{
                  fontSize: "0.875rem",
                  color: "#9ca3af",
                  marginBottom: "1rem",
                  marginTop: "0.25rem",
                }}
              >
                Connect at least one of these tools to satisfy this requirement
              </p>
              <div className={vcStyles.integrationGrid}>
                {categoryIntegrations.map(renderIntegrationCard)}
              </div>
            </div>
          );
        })}

      {/* All integrations — flat paginated grid (with search + Composio) */}
      {(() => {
        const allVc = [...UNIQUE_INTEGRATIONS, ...composioToolkits];
        const filtered = vcSearchTerm
          ? allVc.filter((i) => i.name.toLowerCase().includes(vcSearchTerm.toLowerCase()))
          : allVc;
        const vcTotalPages = Math.ceil(filtered.length / vcItemsPerPage);
        const vcStart = (vcCurrentPage - 1) * vcItemsPerPage;
        const vcPaginated = filtered.slice(vcStart, vcStart + vcItemsPerPage);

        return (
          <>
            <div style={{ fontSize: "13px", color: "#999", marginBottom: "8px", marginTop: requiredCategories.length > 0 ? "24px" : "0" }}>
              {vcSearchTerm ? `${filtered.length} results` : `Showing ${vcStart + 1}–${Math.min(vcStart + vcItemsPerPage, filtered.length)} of ${filtered.length} integrations`}
            </div>
            <div className={vcStyles.integrationGrid}>
              {vcPaginated.map(renderIntegrationCard)}
            </div>

            {filtered.length === 0 && (
              <div className={styles.noResults}>
                No integrations found matching &ldquo;{vcSearchTerm}&rdquo;
              </div>
            )}

            {vcTotalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px", marginTop: "16px" }}>
                <button
                  onClick={() => setVcCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={vcCurrentPage === 1}
                  style={{ padding: "6px 14px", fontSize: "13px", background: "transparent", color: vcCurrentPage === 1 ? "#555" : "#fff", border: "1px solid #333", borderRadius: "4px", cursor: vcCurrentPage === 1 ? "not-allowed" : "pointer" }}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(vcTotalPages, 5) }, (_, i) => {
                  let page: number;
                  if (vcTotalPages <= 5) page = i + 1;
                  else if (vcCurrentPage <= 3) page = i + 1;
                  else if (vcCurrentPage >= vcTotalPages - 2) page = vcTotalPages - 4 + i;
                  else page = vcCurrentPage - 2 + i;
                  return (
                    <button
                      key={page}
                      onClick={() => setVcCurrentPage(page)}
                      style={{
                        padding: "6px 12px", fontSize: "13px",
                        background: vcCurrentPage === page ? "#7C3AED" : "transparent",
                        color: vcCurrentPage === page ? "#fff" : "#999",
                        border: "1px solid #333", borderRadius: "4px", cursor: "pointer",
                        fontWeight: vcCurrentPage === page ? 700 : 400,
                      }}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setVcCurrentPage((p) => Math.min(vcTotalPages, p + 1))}
                  disabled={vcCurrentPage === vcTotalPages}
                  style={{ padding: "6px 14px", fontSize: "13px", background: "transparent", color: vcCurrentPage === vcTotalPages ? "#555" : "#fff", border: "1px solid #333", borderRadius: "4px", cursor: vcCurrentPage === vcTotalPages ? "not-allowed" : "pointer" }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        );
      })()}

      {connectedProviders.length > 0 && (
        <div className={styles.connectedSection}>
          <div className={styles.connectedHeader}>
            Connected ({connectedProviders.length})
          </div>
          <div className={styles.connectedList}>
            {connectedProviders.map((pid) => {
              const integration = UNIQUE_INTEGRATIONS.find(
                (i) => i.id.toLowerCase() === pid.toLowerCase(),
              );
              const displayName = integration?.name || pid;
              return (
                <div key={pid} className={styles.connectedItem}>
                  <span className={styles.connectedName}>{displayName}</span>
                  <span className={styles.connectedStatus}>
                    <span className={styles.connectedLabel}>Connected</span>
                    <span aria-hidden>⚡</span>
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <button
          className={styles.secondaryButton}
          onClick={() => setCurrentScreen(1)}
        >
          Back
        </button>
        <button
          className={styles.primaryButton}
          onClick={handleNext}
          disabled={
            isSubmitting ||
            connectedProviders.length < MINIMUM_INTEGRATIONS_REQUIRED
          }
        >
          {isSubmitting ? <InlineSpinner /> : "Next"}
        </button>
      </div>
    </div>
  );

  return currentScreen === 1
    ? renderInformativeScreen()
    : renderIntegrationSelectionScreen();
};

export default SelectIntegrationsVC;
